import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface DraftPreviewCardProps extends Omit<BoxProps, 'title'> {
  title: string
  /** Cover image URL; when absent shows the placeholder caption */
  coverUrl?: string
  placeholder?: string
  /** Mono stats line, e.g. "3 PHOTOS · PRIVATE" */
  meta: string
}

const Card = styled(Box)({
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-lg)',
  overflow: 'hidden',
  boxShadow: 'var(--ds-shadow-lift)',
})

const Cover = styled(Box)({
  position: 'relative',
  aspectRatio: '4 / 2.9',
  background: 'var(--ds-hover)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
})

const Placeholder = styled('span')({
  fontSize: 12,
  color: 'var(--ds-ink-subtle)',
  padding: '0 16px',
  textAlign: 'center',
})

const CoverImg = styled('img')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
})

const Meta = styled(Box)({
  padding: '14px 16px 16px',
})

const Name = styled('div')({
  fontSize: 15,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  color: 'var(--ds-ink)',
  minHeight: 20,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const Stats = styled('div')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.08em',
  color: 'var(--ds-ink-subtle)',
  marginTop: 4,
})

/** Live draft card for create flows — cover + title + mono stats, library-card silhouette. */
export function DraftPreviewCard({
  title,
  coverUrl,
  placeholder = 'Cover appears with your first photo',
  meta,
  ...rest
}: DraftPreviewCardProps) {
  return (
    <Card {...rest}>
      <Cover>
        {coverUrl ? (
          <CoverImg src={coverUrl} alt='' />
        ) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
      </Cover>
      <Meta>
        <Name>{title || 'Untitled album'}</Name>
        <Stats>{meta}</Stats>
      </Meta>
    </Card>
  )
}
